import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { teacherModuleAPI } from '../../services/api';
import StudentAssessmentEditor, {
  buildAllModuleStates,
  flattenAssessmentCatalog,
  modulesForSave,
} from './StudentAssessmentEditor';

export default function StudentAssessmentEditDialog({ open, reportId, onClose }) {
  const queryClient = useQueryClient();
  const [modules, setModules] = useState([]);
  const [overallRemarks, setOverallRemarks] = useState('');
  const [readyFor, setReadyFor] = useState(null);

  const detailQuery = useQuery(
    ['studentAssessmentSubmission', reportId],
    async () => {
      const [detailRes, catalogRes] = await Promise.all([
        teacherModuleAPI.getStudentAssessmentSubmission(reportId),
        teacherModuleAPI.getStudentAssessmentCatalog(),
      ]);
      return {
        detail: detailRes.data?.data,
        catalog: catalogRes.data?.data || [],
      };
    },
    { enabled: open && !!reportId }
  );

  const detail = detailQuery.data?.detail;
  const catalog = useMemo(() => detailQuery.data?.catalog || [], [detailQuery.data?.catalog]);
  const report = detail?.report;
  const student = detail?.student;
  const studentName = `${student?.firstName || ''} ${student?.lastName || ''}`.trim();

  useEffect(() => {
    if (!open || !report || detailQuery.isLoading) return;
    const flat = flattenAssessmentCatalog(catalog);
    if (!flat.length) return;
    if (readyFor === reportId) return;
    setModules(buildAllModuleStates(flat, report.assessmentData));
    setOverallRemarks(report.assessmentData?.overallRemarks || report.remarks || '');
    setReadyFor(reportId);
  }, [open, report, catalog, reportId, readyFor, detailQuery.isLoading]);

  useEffect(() => {
    if (!open) setReadyFor(null);
  }, [open]);

  const saveMutation = useMutation(
    () =>
      teacherModuleAPI.updateStudentAssessmentSubmission(reportId, {
        remarks: overallRemarks,
        assessmentData: { modules: modulesForSave(modules), overallRemarks },
      }),
    {
      onSuccess: (res) => {
        const parentStatus = res.data?.parentReportStatus;
        toast.success(
          parentStatus === 'sent'
            ? 'Report updated. The email already sent to the parent is unchanged.'
            : 'Report updated'
        );
        queryClient.invalidateQueries(['studentAssessmentSubmissions']);
        queryClient.invalidateQueries(['studentAssessmentSubmission', reportId]);
        queryClient.invalidateQueries(['parentMonthlyReportsQueue']);
        queryClient.invalidateQueries(['studentMonthlyReportDetail']);
        onClose();
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Could not save the report'),
    }
  );

  const handleSave = () => {
    if (!modulesForSave(modules).length) {
      toast.error('Fill at least one row before saving');
      return;
    }
    saveMutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Edit approved report{studentName ? ` — ${studentName}` : ''}</DialogTitle>
      <DialogContent dividers>
        {detailQuery.isLoading ? (
          <CircularProgress />
        ) : detailQuery.isError ? (
          <Alert severity="error">
            {detailQuery.error?.response?.data?.message || 'Could not load this report'}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Saving keeps this report approved.
              {detail?.parentReportStatus === 'sent'
                ? ' Parents already received it, so that email stays as sent.'
                : ' The parent preview updates if it has not been sent yet.'}
            </Typography>
            <StudentAssessmentEditor
              catalog={catalog}
              modules={modules}
              overallRemarks={overallRemarks}
              readOnly={false}
              onChangeSelection={(moduleId, parameterId, optionId) => {
                setModules((prev) =>
                  prev.map((m) => {
                    if (m.moduleId !== moduleId) return m;
                    return {
                      ...m,
                      selections: m.selections.map((s) =>
                        s.parameterId === parameterId ? { ...s, optionId } : s
                      ),
                    };
                  })
                );
              }}
              onChangeModuleRemarks={(moduleId, remarks) => {
                setModules((prev) =>
                  prev.map((m) => {
                    if (m.moduleId !== moduleId) return m;
                    return {
                      ...m,
                      remarks,
                      sendCustomNotesOnly: remarks.trim() ? Boolean(m.sendCustomNotesOnly) : false,
                    };
                  })
                );
              }}
              onChangeSendCustomNotesOnly={(moduleId, sendCustomNotesOnly) => {
                setModules((prev) =>
                  prev.map((m) => (m.moduleId === moduleId ? { ...m, sendCustomNotesOnly } : m))
                );
              }}
              onChangeOverallRemarks={setOverallRemarks}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={detailQuery.isLoading || detailQuery.isError || saveMutation.isLoading || !modules.length}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
